import { 
  Users, 
  TrendingUp, 
  Target, 
  BarChart3, 
  Award,
  Brain,
  Zap,
  Clock,
  Calendar,
  Heart,
  BookOpen,
  Star,
  Settings,
  UserCheck,
  Shield,
  Lock,
  Database,
  Network,
  CheckCircle,
  Globe,
  Activity,
  Eye,
  Rocket,
  MessageSquare,
  Lightbulb,
  GraduationCap,
  DollarSign,
  Home,
  Briefcase,
  Smile,
  FileText,
  MapPin,
  Headphones,
  PieChart,
  Workflow,
  RefreshCw,
  Search
} from 'lucide-react';
import type { ProductConfigs } from './types';
import { enterpriseSecurityTemplate } from './enterprise-security-template';

export const missingHrSolutionsConfigs: ProductConfigs = {
  // Performance Management Platform
  'performance-management-platform': {
    keyBenefits: [
      {
        icon: Target,
        title: 'OKR & Goal Management Framework',
        description: 'Enterprise-grade OKR and goal-setting framework with cascading objectives, key results tracking, and real-time progress visibility across departments.',
        metrics: '95% goal alignment achieved',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Continuous Performance Feedback',
        description: 'Real-time feedback loops replacing annual reviews with ongoing performance conversations, peer recognition, and manager coaching moments.',
        metrics: '40% improvement in engagement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: '360-Degree Feedback System',
        description: 'Comprehensive multi-rater feedback from peers, managers, direct reports, and stakeholders with anonymity controls and actionable insights.',
        metrics: '360° perspective coverage',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Performance Insights',
        description: 'Machine learning algorithms identify high performers, predict potential issues, and recommend personalized development interventions.',
        metrics: '85% prediction accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Award,
        title: 'Competency Framework Management',
        description: 'Define and track role-specific competencies with proficiency levels, gap analysis, and development path recommendations.',
        metrics: 'Complete competency mapping',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Performance Analytics Dashboard',
        description: 'Real-time dashboards showing individual, team, and organizational performance metrics with trend analysis and benchmarking.',
        metrics: 'Real-time performance visibility',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Calendar,
        title: 'Flexible Review Cycles',
        description: 'Configure custom review cycles—quarterly, bi-annual, annual, or project-based—with automated reminders and escalations.',
        metrics: 'Flexible review scheduling',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Lightbulb,
        title: 'Development Planning Integration',
        description: 'Seamlessly connect performance outcomes to individual development plans, learning paths, and career progression opportunities.',
        metrics: 'Integrated development paths',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: MessageSquare,
        title: 'Check-In & 1:1 Management',
        description: 'Structured one-on-one meeting templates, agenda management, action item tracking, and conversation history.',
        metrics: 'Effective 1:1 conversations',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Star,
        title: 'Recognition & Rewards Integration',
        description: 'Peer-to-peer recognition linked to performance outcomes with points, badges, and reward marketplace integration.',
        metrics: 'Engagement boost via recognition',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: FileText,
        title: 'Performance Documentation',
        description: 'Complete audit trail of performance conversations, feedback, ratings, and decisions for compliance and dispute resolution.',
        metrics: 'Full documentation trail',
        color: 'from-slate-500 to-gray-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language & Global Support',
        description: 'Support for international organizations with multi-language interfaces, regional compliance, and timezone awareness.',
        metrics: '50+ languages supported',
        color: 'from-indigo-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Performance Modeling',
        description: 'AI models predict future performance trajectories, identify retention risks, and recommend proactive interventions.',
        metrics: '87% risk prediction accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Feedback Nudges',
        description: 'Intelligent prompts encourage managers and peers to provide timely feedback after key events and milestones.',
        metrics: '3x more feedback given',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: PieChart,
        title: 'Calibration & Rating Distribution',
        description: 'Ensure fair ratings with calibration sessions, forced distribution analysis, and manager alignment tools.',
        metrics: 'Fair rating distribution',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Workflow,
        title: 'Custom Workflow Automation',
        description: 'Build automated workflows for review cycles, approvals, escalations, and notifications without coding.',
        metrics: '500+ automation triggers',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Search,
        title: 'Natural Language Feedback Analysis',
        description: 'NLP analysis of qualitative feedback to identify themes, sentiment, and development opportunities.',
        metrics: 'AI-powered text analysis',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Improvement Loops',
        description: 'Closed-loop systems connect performance data to learning recommendations and track improvement over time.',
        metrics: 'Continuous improvement cycle',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Benchmarking & Comparisons',
        description: 'Compare performance against industry benchmarks, internal teams, and historical baselines.',
        metrics: 'Industry benchmarking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Configurable Rating Scales',
        description: 'Customize rating scales, competency definitions, and evaluation criteria to match your culture.',
        metrics: 'Fully configurable scales',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Team Performance Insights',
        description: 'Aggregate individual performance data to surface team dynamics, collaboration patterns, and leadership effectiveness.',
        metrics: 'Team-level analytics',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Network,
        title: 'HRIS & Payroll Integration',
        description: 'Seamless integration with payroll, compensation, and HRIS systems for merit increases and bonus calculations.',
        metrics: '50+ HRIS integrations',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: GraduationCap,
        title: 'Learning Recommendations',
        description: 'AI-generated learning recommendations based on performance gaps, career goals, and skill requirements.',
        metrics: 'Personalized learning paths',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Eye,
        title: 'Manager Dashboards',
        description: 'Dedicated manager views with team performance snapshots, pending actions, and coaching suggestions.',
        metrics: 'Manager-focused insights',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Employee Engagement & Sentiment Platform
  'employee-engagement-sentiment-platform': {
    keyBenefits: [
      {
        icon: Heart,
        title: 'Real-Time Engagement Measurement',
        description: 'Continuous pulse surveys and sentiment tracking providing instant visibility into employee engagement levels across the organization.',
        metrics: '92% survey response rate',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Sentiment Analysis',
        description: 'Natural language processing analyzes open-ended feedback to detect emotion, satisfaction, and emerging concerns in real-time.',
        metrics: '95% sentiment accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Smile,
        title: 'Mood & Wellbeing Tracking',
        description: 'Daily mood check-ins and wellbeing assessments with early warning detection for burnout and disengagement risks.',
        metrics: 'Early burnout detection',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: TrendingUp,
        title: 'Engagement Driver Analysis',
        description: 'Identify the key drivers of engagement specific to your organization with correlation analysis and impact scoring.',
        metrics: 'Key drivers identified',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Anonymous Feedback Channels',
        description: 'Secure anonymous feedback systems encouraging honest input with intelligent response aggregation and theme detection.',
        metrics: '100% anonymity guaranteed',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Star,
        title: 'Recognition & Rewards Program',
        description: 'Peer-to-peer recognition platform with points, badges, rewards marketplace, and social celebration feeds.',
        metrics: '5x recognition increase',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Culture Analytics Dashboard',
        description: 'Comprehensive culture health metrics with trending analysis, department comparisons, and benchmark insights.',
        metrics: 'Culture health visibility',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: MessageSquare,
        title: 'Two-Way Communication Tools',
        description: 'Company-wide announcements, team updates, and Q&A forums with engagement tracking and reach analytics.',
        metrics: 'Enhanced internal comms',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Action Planning & Follow-Up',
        description: 'Transform insights into action with guided planning tools, accountability tracking, and impact measurement.',
        metrics: 'Insight-to-action conversion',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'DEI Survey Modules',
        description: 'Specialized diversity, equity, and inclusion survey modules with demographic analysis and inclusion scoring.',
        metrics: 'DEI insights included',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Clock,
        title: 'Lifecycle Surveys',
        description: 'Automated surveys at key employee journey moments—onboarding, anniversaries, role changes, and exit.',
        metrics: 'Full lifecycle coverage',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Lightbulb,
        title: 'Manager Coaching Insights',
        description: 'Personalized coaching recommendations for managers based on team engagement data and leadership effectiveness scores.',
        metrics: 'Manager development support',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Predictive Attrition Modeling',
        description: 'Machine learning models predict flight risk based on engagement patterns, enabling proactive retention interventions.',
        metrics: '85% attrition prediction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Alert System',
        description: 'Instant notifications when engagement scores drop, negative sentiment spikes, or critical feedback is received.',
        metrics: 'Instant issue detection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: PieChart,
        title: 'Text Analytics & Theme Detection',
        description: 'AI-powered analysis of open-ended responses to automatically categorize themes, topics, and emerging issues.',
        metrics: 'Automated theme analysis',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Search,
        title: 'Intelligent Question Library',
        description: 'Scientifically validated question bank with smart recommendations based on your goals and previous results.',
        metrics: '500+ validated questions',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Workflow,
        title: 'Survey Automation Engine',
        description: 'Automated survey scheduling, reminders, and distribution with smart timing optimization for maximum response rates.',
        metrics: 'Automated distribution',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Network,
        title: 'Organizational Network Analysis',
        description: 'Map informal networks, collaboration patterns, and information flows to understand cultural dynamics.',
        metrics: 'Network insights',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Industry Benchmarking',
        description: 'Compare your engagement metrics against industry benchmarks, best-in-class companies, and regional norms.',
        metrics: 'Industry comparisons',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'White-Label & Branding',
        description: 'Fully customizable branding, email templates, and survey interfaces to match your company identity.',
        metrics: 'Custom branding',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: FileText,
        title: 'Executive Reporting Suite',
        description: 'Board-ready reports with executive summaries, trend analysis, and strategic recommendations.',
        metrics: 'Executive-ready reports',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Listening Platform',
        description: 'Always-on feedback channels with sentiment monitoring across multiple touchpoints and communication platforms.',
        metrics: 'Continuous listening',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Focus Group Facilitation',
        description: 'Tools for organizing and conducting virtual focus groups with automated transcription and analysis.',
        metrics: 'Virtual focus groups',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Eye,
        title: 'Manager Self-Service Portal',
        description: 'Dedicated manager dashboards with team insights, action suggestions, and progress tracking.',
        metrics: 'Manager empowerment',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Employee Learning & Development Platform
  'employee-learning-development-platform': {
    keyBenefits: [
      {
        icon: GraduationCap,
        title: 'AI-Powered Learning Paths',
        description: 'Personalized learning journeys automatically generated based on role, skills gaps, career goals, and learning style preferences.',
        metrics: '3x faster skill development',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'Intelligent Content Recommendations',
        description: 'Machine learning algorithms recommend relevant courses, articles, and videos from internal and external content libraries.',
        metrics: '95% recommendation relevance',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: BookOpen,
        title: 'Comprehensive Course Library',
        description: 'Access to 50,000+ courses from top providers including LinkedIn Learning, Coursera, Udemy for Business, and custom content.',
        metrics: '50,000+ courses available',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Skills Assessment & Gap Analysis',
        description: 'Comprehensive skills assessments identifying current proficiency levels and gaps against role requirements and career aspirations.',
        metrics: 'Complete skills visibility',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'Certification & Badge Management',
        description: 'Track professional certifications, issue digital badges, and manage credential verification with blockchain authentication.',
        metrics: 'Verified credentials',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Zap,
        title: 'Microlearning & Mobile First',
        description: 'Bite-sized learning modules optimized for mobile consumption with offline access and smart notification scheduling.',
        metrics: '5-minute micro-lessons',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Users,
        title: 'Social & Collaborative Learning',
        description: 'Peer learning communities, discussion forums, cohort-based courses, and mentorship matching features.',
        metrics: 'Social learning enabled',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: BarChart3,
        title: 'Learning Analytics & ROI',
        description: 'Comprehensive analytics measuring learning engagement, completion rates, skill improvements, and business impact.',
        metrics: 'Measurable learning ROI',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Calendar,
        title: 'Compliance Training Management',
        description: 'Automated compliance training assignments, tracking, and reporting with certification expiry alerts and audit trails.',
        metrics: '100% compliance coverage',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'Custom Content Creation',
        description: 'Built-in authoring tools for creating custom courses, videos, quizzes, and learning paths without technical expertise.',
        metrics: 'No-code content creation',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language Support',
        description: 'Support for 50+ languages with AI-powered translation, localized content, and cultural adaptation features.',
        metrics: '50+ languages supported',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: TrendingUp,
        title: 'Career Path Integration',
        description: 'Link learning directly to career progression with role-based skill requirements and promotion readiness tracking.',
        metrics: 'Career-linked learning',
        color: 'from-violet-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Adaptive Learning Engine',
        description: 'AI adjusts content difficulty, pacing, and format based on individual learning patterns and assessment performance.',
        metrics: 'Personalized adaptation',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Lightbulb,
        title: 'Skills Inference from Work',
        description: 'Automatically infer skill development from work activities, project completions, and system usage patterns.',
        metrics: 'Automatic skill tracking',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Network,
        title: 'External Content Integration',
        description: 'Seamless integration with LinkedIn Learning, Coursera, Pluralsight, and custom content sources via APIs.',
        metrics: '100+ content integrations',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Workflow,
        title: 'Learning Workflow Automation',
        description: 'Automated learning assignments based on role changes, performance reviews, compliance requirements, and manager requests.',
        metrics: 'Automated assignments',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: PieChart,
        title: 'Skill Gap Analytics',
        description: 'Organization-wide skill gap analysis with forecasting, succession planning integration, and talent marketplace insights.',
        metrics: 'Strategic skill planning',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: MessageSquare,
        title: 'AI Learning Assistant',
        description: 'Conversational AI assistant helps learners find content, answer questions, and navigate learning paths.',
        metrics: 'AI-powered support',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Search,
        title: 'Semantic Content Search',
        description: 'Advanced search understanding context and intent to find relevant learning content across all sources.',
        metrics: 'Intelligent search',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: RefreshCw,
        title: 'Spaced Repetition System',
        description: 'Scientific spaced repetition algorithms reinforce learning at optimal intervals for long-term retention.',
        metrics: '40% better retention',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Eye,
        title: 'Manager Learning Dashboards',
        description: 'Dedicated views for managers to track team learning progress, assign content, and monitor skill development.',
        metrics: 'Manager oversight',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Business Impact Tracking',
        description: 'Connect learning investments to business outcomes with correlation analysis and ROI measurement.',
        metrics: 'Business impact proven',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Mentorship Matching',
        description: 'AI-powered mentor matching based on skills, career goals, learning styles, and availability.',
        metrics: 'Smart mentor matching',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Rocket,
        title: 'Gamification Engine',
        description: 'Points, badges, leaderboards, and challenges to drive engagement and healthy competition.',
        metrics: '2x learning engagement',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Skills Mapping Platform
  'skills-mapping-platform': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI-Powered Skills Discovery',
        description: 'Automatically identify and validate employee skills from work artifacts, project contributions, and system usage patterns.',
        metrics: '95% skills identification',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Skills Gap Analysis',
        description: 'Comprehensive gap analysis comparing current capabilities against role requirements, team needs, and strategic objectives.',
        metrics: 'Complete gap visibility',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Users,
        title: 'Project-Skill Matching',
        description: 'Intelligent matching of employees to projects based on skill requirements, availability, and development interests.',
        metrics: '40% better project staffing',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Career Path Planning',
        description: 'Visual career path mapping with skill requirements, learning recommendations, and progression milestones.',
        metrics: 'Clear career progression',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Competency Framework Management',
        description: 'Define and manage role-based competency frameworks with proficiency levels and behavioral indicators.',
        metrics: 'Structured competencies',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Search,
        title: 'Internal Talent Marketplace',
        description: 'Enable employees to discover internal opportunities, projects, and gigs that match their skills and aspirations.',
        metrics: 'Internal mobility enabled',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: BarChart3,
        title: 'Skills Analytics Dashboard',
        description: 'Organization-wide skills inventory with distribution analysis, trending skills, and capability planning insights.',
        metrics: 'Strategic skills visibility',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: GraduationCap,
        title: 'Learning Integration',
        description: 'Connect skills gaps directly to learning content recommendations and development opportunities.',
        metrics: 'Gap-to-learning automation',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Star,
        title: 'Skill Endorsements & Validation',
        description: 'Peer endorsements, manager validations, and assessment-based skill verification for accurate profiling.',
        metrics: 'Validated skill profiles',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Skills Taxonomy Management',
        description: 'Industry-standard and custom skills taxonomies with synonyms, relationships, and hierarchy management.',
        metrics: 'Complete taxonomy control',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Users,
        title: 'Team Composition Analysis',
        description: 'Analyze team skill diversity, redundancy, and gaps to optimize team formation and development investments.',
        metrics: 'Team skill optimization',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Lightbulb,
        title: 'Mentor Matching',
        description: 'Connect employees with mentors based on skill development goals and mentor expertise areas.',
        metrics: 'Smart mentorship matching',
        color: 'from-orange-500 to-red-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Skills Inference Engine',
        description: 'AI automatically infers skills from job titles, work history, certifications, and digital footprint analysis.',
        metrics: 'Automated skill discovery',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Skills Updates',
        description: 'Skills profiles update automatically as employees complete learning, projects, and earn new certifications.',
        metrics: 'Always-current profiles',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Network,
        title: 'Skills Graph Visualization',
        description: 'Interactive visualization of skills relationships, adjacencies, and learning pathways across the organization.',
        metrics: 'Visual skills mapping',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Future Skills Forecasting',
        description: 'Predict emerging skill requirements based on industry trends, technology changes, and strategic plans.',
        metrics: 'Future skills planning',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: PieChart,
        title: 'Workforce Planning Integration',
        description: 'Connect skills data to strategic workforce planning for hiring, development, and organizational design decisions.',
        metrics: 'Strategic workforce insights',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Workflow,
        title: 'Assessment Workflow Automation',
        description: 'Automated skills assessments with configurable frequency, question banks, and certification requirements.',
        metrics: 'Automated assessments',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: RefreshCw,
        title: 'Skill Decay Tracking',
        description: 'Monitor skill currency and identify skills at risk of becoming outdated without recent practice or learning.',
        metrics: 'Skill freshness monitoring',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Proficiency Scales',
        description: 'Define organization-specific proficiency levels with behavioral anchors and progression criteria.',
        metrics: 'Customizable proficiency',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: FileText,
        title: 'Skills Reporting Suite',
        description: 'Comprehensive reports on skills distribution, gaps, trends, and development effectiveness for leadership.',
        metrics: 'Executive reporting',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Search,
        title: 'Expert Finder',
        description: 'Quickly locate subject matter experts across the organization based on validated skills and availability.',
        metrics: 'Expert discovery',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Skills API & Integration',
        description: 'RESTful APIs for integrating skills data with HRIS, learning, performance, and recruiting systems.',
        metrics: 'Full API access',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Eye,
        title: 'Manager Skills Dashboard',
        description: 'Dedicated manager views showing team skills, gaps, development progress, and succession readiness.',
        metrics: 'Manager-focused insights',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Inclusive Hiring Platform
  'inclusive-hiring-platform': {
    keyBenefits: [
      {
        icon: Shield,
        title: 'AI Bias Detection & Removal',
        description: 'Advanced algorithms identify and remove biased language, criteria, and evaluation patterns throughout the hiring process.',
        metrics: '92% bias reduction',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Blind Resume Screening',
        description: 'Automatically anonymize resumes by removing names, photos, addresses, and other identifiable information.',
        metrics: '100% identity anonymization',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: FileText,
        title: 'Inclusive Job Description Analysis',
        description: 'AI-powered analysis of job postings to identify and suggest alternatives for gendered, exclusionary, or biased language.',
        metrics: 'Inclusive language scoring',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Structured Interview Framework',
        description: 'Standardized interview guides, scoring rubrics, and evaluation criteria ensuring fair and consistent candidate assessment.',
        metrics: 'Consistent evaluations',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Diverse Candidate Sourcing',
        description: 'Access diverse talent pools and job boards specifically designed to reach underrepresented candidates.',
        metrics: '40% more diverse candidates',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: BarChart3,
        title: 'Diversity Analytics Dashboard',
        description: 'Real-time visibility into diversity metrics at every hiring stage with funnel analysis and conversion tracking.',
        metrics: 'Full diversity visibility',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Award,
        title: 'Fair Assessment Scoring',
        description: 'Skills-based assessment scoring that focuses on capability rather than background or pedigree.',
        metrics: 'Skills-focused evaluation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: GraduationCap,
        title: 'Interviewer Training Modules',
        description: 'Built-in unconscious bias training and inclusive interviewing certification for all hiring team members.',
        metrics: 'Trained interviewer teams',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: MessageSquare,
        title: 'Inclusive Communication Templates',
        description: 'Pre-written templates for job offers, rejections, and candidate communications using inclusive language.',
        metrics: 'Inclusive messaging',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: CheckCircle,
        title: 'Compliance & Audit Trails',
        description: 'Complete documentation of hiring decisions for EEOC, OFCCP, and other regulatory compliance requirements.',
        metrics: 'Full compliance coverage',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Goal Tracking & Reporting',
        description: 'Set, track, and report on diversity hiring goals with executive dashboards and progress alerts.',
        metrics: 'Goal achievement tracking',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Globe,
        title: 'Global Diversity Support',
        description: 'Localized diversity definitions, protected characteristics, and compliance requirements for global hiring.',
        metrics: 'Global DEI support',
        color: 'from-orange-500 to-red-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Bias Pattern Detection',
        description: 'AI analyzes historical hiring data to identify systemic bias patterns in sourcing, screening, and decision-making.',
        metrics: 'Historical bias analysis',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Bias Alerts',
        description: 'Instant notifications when bias patterns are detected in evaluations, interview feedback, or hiring decisions.',
        metrics: 'Live bias detection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: PieChart,
        title: 'Pipeline Diversity Analysis',
        description: 'Detailed analysis of diversity drop-off points in the hiring funnel with root cause identification.',
        metrics: 'Funnel optimization',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Network,
        title: 'ATS Integration',
        description: 'Seamless integration with existing ATS systems including Greenhouse, Lever, Workday, and iCIMS.',
        metrics: '50+ ATS integrations',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Search,
        title: 'Skill-Based Matching',
        description: 'Match candidates to roles based on verified skills rather than proxies like education or company names.',
        metrics: 'Skills-first matching',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Custom Bias Rules Engine',
        description: 'Define organization-specific bias detection rules and inclusive hiring criteria.',
        metrics: 'Customizable rules',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Workflow,
        title: 'Diverse Interview Panel Automation',
        description: 'Automatically ensure interview panels meet diversity requirements with smart scheduling optimization.',
        metrics: 'Diverse panels ensured',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Benchmarking & Industry Comparisons',
        description: 'Compare your diversity metrics against industry benchmarks and best-in-class organizations.',
        metrics: 'Industry benchmarking',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: FileText,
        title: 'EEOC & OFCCP Reporting',
        description: 'Automated generation of EEO-1, VETS-4212, and other required compliance reports.',
        metrics: 'Compliance automation',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Eye,
        title: 'Candidate Experience Surveys',
        description: 'Gather diversity-focused feedback from candidates about their hiring experience.',
        metrics: 'Candidate feedback',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Improvement Tracking',
        description: 'Track improvements in diversity metrics over time with trend analysis and goal progress.',
        metrics: 'Improvement tracking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Lightbulb,
        title: 'Best Practice Recommendations',
        description: 'AI-generated recommendations for improving diversity at each stage based on your data and industry research.',
        metrics: 'Data-driven suggestions',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Employee Performance Analytics
  'employee-performance-analytics': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Real-Time Performance Visibility',
        description: 'Live dashboards showing individual, team, and organizational performance metrics with trend analysis and anomaly detection.',
        metrics: 'Real-time performance data',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'Predictive Performance Modeling',
        description: 'AI algorithms predict future performance trajectories, identify high-potential employees, and flag at-risk performers.',
        metrics: '88% prediction accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Trend Analysis',
        description: 'Historical performance tracking with pattern recognition to identify improvement or decline trajectories early.',
        metrics: 'Early trend detection',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Goal Achievement Tracking',
        description: 'Real-time visibility into goal progress with automated alerts, milestone tracking, and completion forecasting.',
        metrics: '95% goal visibility',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Peer Performance Benchmarking',
        description: 'Compare individual performance against peers, teams, departments, and organizational averages.',
        metrics: 'Comprehensive benchmarks',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Award,
        title: 'High Performer Identification',
        description: 'Data-driven identification of top performers with pattern analysis revealing success factors and behaviors.',
        metrics: 'Success pattern discovery',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Search,
        title: 'Development Need Identification',
        description: 'Automatically identify skill gaps and development needs from performance data, feedback, and assessments.',
        metrics: 'Targeted development',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Lightbulb,
        title: 'Manager Coaching Insights',
        description: 'AI-generated coaching recommendations for managers based on team member performance patterns.',
        metrics: 'Data-driven coaching',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Retention Risk Prediction',
        description: 'Early warning system for flight risk based on performance changes, engagement signals, and market factors.',
        metrics: '85% retention risk accuracy',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: PieChart,
        title: 'Team Performance Dynamics',
        description: 'Analyze team composition effects on performance with collaboration pattern and synergy insights.',
        metrics: 'Team dynamics analysis',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: FileText,
        title: 'Executive Performance Reports',
        description: 'Board-ready performance summaries with strategic insights, trends, and recommendations.',
        metrics: 'Executive reporting',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Clock,
        title: 'Performance Review Automation',
        description: 'Pre-populated review forms with performance data, achievements, and development suggestions.',
        metrics: '70% review prep saved',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Machine Learning Models',
        description: 'Custom ML models trained on your organizational data for highly accurate performance predictions.',
        metrics: 'Custom AI models',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Alert Engine',
        description: 'Configurable alerts for performance changes, goal milestones, and risk indicators.',
        metrics: 'Instant notifications',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Network,
        title: 'Data Integration Hub',
        description: 'Connect performance data from HRIS, project management, CRM, and other business systems.',
        metrics: '100+ data sources',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: PieChart,
        title: 'Correlation Analysis',
        description: 'Discover correlations between performance and factors like tenure, training, management style, and team composition.',
        metrics: 'Factor correlation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Workflow,
        title: 'Automated Insight Distribution',
        description: 'Push relevant performance insights to managers, HRBP, and employees automatically.',
        metrics: 'Automated distribution',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Custom Metrics Builder',
        description: 'Create organization-specific performance metrics and KPIs with flexible calculation logic.',
        metrics: 'Custom metrics',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Eye,
        title: 'Manager Analytics Portal',
        description: 'Dedicated manager dashboards with team performance trends, individual insights, and action recommendations.',
        metrics: 'Manager empowerment',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Industry Benchmarking',
        description: 'Compare performance metrics against industry standards and best-in-class organizations.',
        metrics: 'Industry comparisons',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: RefreshCw,
        title: 'Continuous Feedback Integration',
        description: 'Incorporate real-time feedback, recognition, and check-in data into performance analytics.',
        metrics: 'Continuous data flow',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: FileText,
        title: 'Custom Report Builder',
        description: 'Drag-and-drop report builder with scheduled distribution and multiple export formats.',
        metrics: 'Flexible reporting',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Database,
        title: 'Data Export & API',
        description: 'Full data export capabilities and RESTful APIs for integration with BI tools and data warehouses.',
        metrics: 'Full data access',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Shield,
        title: 'Role-Based Data Access',
        description: 'Granular permissions ensuring managers see only their team data with appropriate privacy controls.',
        metrics: 'Privacy-first access',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  },

  // Workforce Planning Tool
  'workforce-planning-tool': {
    keyBenefits: [
      {
        icon: MapPin,
        title: 'Strategic Workforce Modeling',
        description: 'Build comprehensive workforce models connecting business strategy to talent requirements with scenario planning capabilities.',
        metrics: 'Strategic alignment',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Talent Demand Forecasting',
        description: 'AI-powered forecasting of future talent needs based on business growth, attrition, and market conditions.',
        metrics: '90% forecast accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Brain,
        title: 'Skills & Competency Planning',
        description: 'Project future skill requirements and identify gaps based on technology changes and strategic initiatives.',
        metrics: 'Future skills visibility',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: PieChart,
        title: 'Scenario Planning & What-If',
        description: 'Model multiple scenarios—growth, downsizing, restructuring—to understand workforce implications.',
        metrics: 'Unlimited scenarios',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Succession Planning Integration',
        description: 'Connect workforce plans to succession pipelines ensuring critical role coverage and leadership continuity.',
        metrics: 'Succession readiness',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: DollarSign,
        title: 'Budget & Cost Planning',
        description: 'Model workforce costs including salaries, benefits, training, and location factors for financial planning.',
        metrics: 'Cost modeling included',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Target,
        title: 'Hiring Plan Generation',
        description: 'Automatically generate hiring plans with timing, volume, and skill requirements based on workforce gaps.',
        metrics: 'Automated hiring plans',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Clock,
        title: 'Retirement & Attrition Prediction',
        description: 'Predict retirements and voluntary turnover to proactively plan for knowledge transfer and replacement.',
        metrics: 'Attrition forecasting',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: BarChart3,
        title: 'Workforce Analytics Dashboard',
        description: 'Real-time visibility into workforce composition, trends, costs, and plan progress.',
        metrics: 'Live workforce insights',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Location & Geo Planning',
        description: 'Plan workforce distribution across locations with labor market data and cost comparisons.',
        metrics: 'Location optimization',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Briefcase,
        title: 'Role & Organization Design',
        description: 'Model organizational structures, reporting relationships, and role design aligned with strategy.',
        metrics: 'Org design support',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: FileText,
        title: 'Executive Reporting Suite',
        description: 'Board-ready workforce planning reports with strategic insights and recommendations.',
        metrics: 'Executive visibility',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Planning Engine',
        description: 'Machine learning optimizes workforce plans considering multiple constraints and objectives simultaneously.',
        metrics: 'AI optimization',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Network,
        title: 'HRIS & Finance Integration',
        description: 'Seamless integration with HR systems and financial planning tools for accurate, real-time data.',
        metrics: '50+ integrations',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Plan Updates',
        description: 'Plans update automatically as actuals change, ensuring forecasts remain accurate and relevant.',
        metrics: 'Live plan updates',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: PieChart,
        title: 'Sensitivity Analysis',
        description: 'Understand how changes in key assumptions impact workforce plans with sensitivity modeling.',
        metrics: 'Impact analysis',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Workflow,
        title: 'Collaborative Planning',
        description: 'Enable cross-functional planning with HR, Finance, and business leaders with version control and approvals.',
        metrics: 'Collaborative workflows',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'External Labor Market Data',
        description: 'Integrate labor market intelligence including salary benchmarks, talent availability, and competitive insights.',
        metrics: 'Market intelligence',
        color: 'from-amber-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Custom Planning Frameworks',
        description: 'Build organization-specific planning methodologies, templates, and approval workflows.',
        metrics: 'Customizable frameworks',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Trend Analysis & Projections',
        description: 'Historical trend analysis with forward projections for headcount, costs, and capability metrics.',
        metrics: 'Trend projections',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Eye,
        title: 'Manager Planning Dashboards',
        description: 'Enable managers to participate in planning with role-appropriate views and input capabilities.',
        metrics: 'Manager participation',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: RefreshCw,
        title: 'Rolling Forecast Support',
        description: 'Maintain rolling 12-18 month forecasts that update continuously with new information.',
        metrics: 'Rolling forecasts',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Plan vs. Actual Tracking',
        description: 'Monitor plan execution with variance analysis and automatic reforecasting recommendations.',
        metrics: 'Variance tracking',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Search,
        title: 'Internal Mobility Optimization',
        description: 'Identify opportunities to fill gaps through internal moves, redeployment, and reskilling.',
        metrics: 'Internal-first planning',
        color: 'from-emerald-500 to-green-600'
      }
    ],
    securityPerformance: enterpriseSecurityTemplate
  }
};
